export const portlessWebUrl = 'https://join-os.localhost';
export const portlessWholesaleUrl = 'https://wholesale.join-os.localhost';

export const baseUrl = (): string => {
  // In the browser, use current origin dynamically (works for any URL: previews, production, local)
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  // Server-side: prefer explicitly configured public site URL
  if (process.env.VERCEL_ENV === 'production' || process.env.NEXT_PUBLIC_SITE_URL) {
    return process.env.NEXT_PUBLIC_SITE_URL || '';
  }

  // Use Vercel preview URLs (branch or commit-based)
  if (process.env.VERCEL_BRANCH_URL) {
    return `https://${process.env.VERCEL_BRANCH_URL}`;
  }

  if (process.env.VERCEL_URL) {
    return `https://${process.env.VERCEL_URL}`;
  }

  if (process.env.PORTLESS_URL) {
    return process.env.PORTLESS_URL;
  }

  // Fallback to localhost for local development
  return `http://localhost:${process.env.PORT ?? 3000}`;
};

export type AppContext = 'web' | 'wholesale';

/**
 * Returns the appropriate base URL for a given app context.
 *
 * Note: email generation happens server-side, so `window.location.origin` is not available.
 * Configure wholesale with `NEXT_PUBLIC_WHOLESALE_URL`.
 */
export function appBaseUrl(appContext: AppContext): string {
  if (appContext === 'wholesale') {
    if (process.env.PORTLESS_URL) return portlessWholesaleUrl;
    return process.env.NEXT_PUBLIC_WHOLESALE_URL || baseUrl();
  }
  return baseUrl();
}

export function localDevelopmentUrls(): string[] {
  if (process.env.VERCEL_ENV === 'production' || process.env.NODE_ENV === 'production') {
    return [];
  }

  return ['http://localhost:3000', 'http://localhost:3001', portlessWebUrl, portlessWholesaleUrl];
}

export function previewUrls(): string[] {
  const isProduction = process.env.VERCEL_ENV === 'production';

  if (isProduction) {
    const productionUrl = process.env.NEXT_PUBLIC_SITE_URL;
    // Rolling releases expose the exact current deployment through VERCEL_URL.
    // Do not trust VERCEL_BRANCH_URL or a *.vercel.app wildcard in production.
    const deploymentUrl = process.env.VERCEL_URL ? `https://${process.env.VERCEL_URL}` : undefined;

    return [productionUrl, deploymentUrl].filter((url): url is string => Boolean(url));
  }

  // Use Vercel's dynamic URLs for preview deployments
  const urls: string[] = [];

  // VERCEL_BRANCH_URL: Git branch URL (e.g., join-os-omnichannel-git-develop-oscar-soles.vercel.app)
  if (process.env.VERCEL_BRANCH_URL) {
    urls.push(`https://${process.env.VERCEL_BRANCH_URL}`);
  }

  // VERCEL_URL: Commit-based deployment URL (e.g., join-os-omnichannel-mflmko84b-oscar-soles.vercel.app)
  if (process.env.VERCEL_URL) {
    urls.push(`https://${process.env.VERCEL_URL}`);
  }

  return urls;
}

function isExactHttpsOrigin(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.origin === value;
  } catch {
    return false;
  }
}

/**
 * Exact HTTPS origins explicitly approved for Better Auth via deployment configuration.
 * This intentionally does not infer trust from a Vercel hostname or wildcard.
 */
export function approvedTrustedOrigins(): string[] {
  const configuredOrigins = process.env.BETTER_AUTH_TRUSTED_ORIGINS ?? '';

  return Array.from(
    new Set(
      configuredOrigins
        .split(',')
        .map((origin) => origin.trim())
        .filter(isExactHttpsOrigin),
    ),
  );
}
